import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Autocomplete, TextField, CircularProgress, Box, Typography } from '@mui/material';
import { debounce } from 'lodash';

// Module-level defaults => stable identities across renders
const EMPTY_ARRAY = [];
const defaultGetResults = (response) => response?.data?.data || [];
const defaultGetPagination = (response) => {
    const pag = response?.data?.pagination;
    return { hasMore: pag ? pag.page < pag.pages : false };
};
const defaultGetOptionLabel = (option) => option?.name || '';
const defaultIsOptionEqualToValue = (option, val) =>
    !!option && !!val && option._id === val._id;
const passthroughFilter = (x) => x; // server-side filtering only

const OptimizedSearchableAutocomplete = ({
    label,
    searchFunction,
    value,
    onChange,
    getOptionLabel,
    isOptionEqualToValue,
    renderOption,
    disabled = false,
    error,
    helperText,
    placeholder,
    noOptionsText = 'No results found',
    minSearchLength = 2,
    searchDelay = 400,
    initialOptions = EMPTY_ARRAY,
    maxOptions = 20,
    getResults = defaultGetResults,
    getPagination = defaultGetPagination,
    ...otherProps
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [options, setOptions] = useState(initialOptions);
    const [loading, setLoading] = useState(false);
    const [inputValue, setInputValue] = useState(() =>
        value ? (getOptionLabel || defaultGetOptionLabel)(value) : ''
    );

    // Always-fresh props, readable from stable callbacks without
    // making those callbacks (or the debounce) depend on prop identity.
    const latest = useRef({});
    latest.current = {
        searchFunction,
        getResults,
        getPagination,
        maxOptions,
        minSearchLength,
        initialOptions,
        getOptionLabel: getOptionLabel || defaultGetOptionLabel,
    };

    const currentSearchTerm = useRef('');
    const pageRef = useRef(1);
    const hasMoreRef = useRef(false);
    const loadingRef = useRef(false);
    const requestIdRef = useRef(0); // ignore stale responses

    // ── Run a search (page 1 replaces, page > 1 appends) ─────────────────
    const runSearch = useCallback(async (term, pageNum) => {
        const { searchFunction: fn, getResults: toResults, getPagination: toPagination, maxOptions: limit } =
            latest.current;
        if (!fn) return;

        const requestId = ++requestIdRef.current;
        loadingRef.current = true;
        setLoading(true);

        try {
            const response = await fn(term, { page: pageNum, limit });
            if (requestId !== requestIdRef.current) return; // a newer request superseded this one

            const results = toResults(response) || [];
            const { hasMore } = toPagination(response);

            setOptions((prev) => {
                if (pageNum === 1) return results;
                const existingIds = new Set(prev.map((o) => o?._id));
                return [...prev, ...results.filter((r) => r?._id && !existingIds.has(r._id))];
            });

            pageRef.current = pageNum;
            hasMoreRef.current = !!hasMore;
        } catch (err) {
            if (requestId !== requestIdRef.current) return;
            console.error('Search error:', err);
            // keep existing options on error
        } finally {
            if (requestId === requestIdRef.current) {
                loadingRef.current = false;
                setLoading(false);
            }
        }
    }, []);

    // ── Debounced search: created once per searchDelay, NOT per initialOptions ──
    const debouncedSearch = useMemo(
        () =>
            debounce((term) => {
                currentSearchTerm.current = term;
                pageRef.current = 1;

                const { minSearchLength: min, initialOptions: initial } = latest.current;

                if (term.length >= min) {
                    hasMoreRef.current = true;
                    runSearch(term, 1);
                } else {
                    // Below min length: show the preloaded list again
                    requestIdRef.current += 1; // drop any in-flight response
                    loadingRef.current = false;
                    setLoading(false);
                    hasMoreRef.current = false;
                    setOptions(initial);
                }
            }, searchDelay),
        [searchDelay, runSearch]
    );

    useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);
    useEffect(() => () => { requestIdRef.current += 1; }, []); // ignore responses after unmount

    // ── Sync preloaded list, but only when its CONTENT changes and the user
    //    isn't currently looking at search results ──────────────────────────
    const initialKey = useMemo(
        () => initialOptions.map((o) => o?._id).join('|'),
        [initialOptions]
    );

    useEffect(() => {
        const term = currentSearchTerm.current;
        if (term && term.length >= latest.current.minSearchLength) return; // don't clobber search results
        setOptions(latest.current.initialOptions);
    }, [initialKey]);

    // ── Selected value must always be selectable: merge at render time ────
    const displayOptions = useMemo(() => {
        if (value?._id && !options.some((o) => o?._id === value._id)) {
            return [value, ...options];
        }
        return options;
    }, [options, value]);

    // ── Keep the text in sync with the selected value, without wiping what
    //    the user is typing when nothing was selected/cleared ──────────────
    const valueId = value?._id ?? null;
    const prevValueIdRef = useRef(valueId);

    useEffect(() => {
        if (valueId) {
            setInputValue(latest.current.getOptionLabel(value));
        } else if (prevValueIdRef.current) {
            setInputValue(''); // value was cleared
        }
        prevValueIdRef.current = valueId;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [valueId]);

    // ── On open: make sure there's something to show ──────────────────────
    useEffect(() => {
        if (!isOpen) return;
        const { minSearchLength: min, initialOptions: initial } = latest.current;

        if (min === 0 && !currentSearchTerm.current) {
            debouncedSearch('');
        } else if (options.length === 0) {
            setOptions(initial);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    const handleInputChange = useCallback(
        (event, newInputValue, reason) => {
            setInputValue(newInputValue);

            if (reason === 'input') {
                debouncedSearch(newInputValue);
            } else if (reason === 'clear') {
                debouncedSearch.cancel();
                currentSearchTerm.current = '';
                pageRef.current = 1;
                hasMoreRef.current = false;
                requestIdRef.current += 1;
                loadingRef.current = false;
                setLoading(false);
                setOptions(latest.current.initialOptions);
            }
        },
        [debouncedSearch]
    );

    // ── Infinite scroll ───────────────────────────────────────────────────
    const handleScroll = useCallback(
        (event) => {
            const node = event.currentTarget;
            const distanceFromBottom = node.scrollHeight - (node.scrollTop + node.clientHeight);
            if (distanceFromBottom > 50) return;
            if (loadingRef.current || !hasMoreRef.current) return;
            runSearch(currentSearchTerm.current, pageRef.current + 1);
        },
        [runSearch]
    );

    // ── Highlight matches (split with a capture group: odd indexes = matches) ──
    const highlightText = useCallback((text, searchTerm) => {
        if (!searchTerm?.trim() || !text) return text;
        try {
            const escaped = searchTerm.trim().replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            const parts = text.toString().split(new RegExp(`(${escaped})`, 'gi'));
            return parts.map((part, index) =>
                index % 2 === 1 ? (
                    <Box component="span" key={index} sx={{ fontWeight: 'bold' }}>
                        {part}
                    </Box>
                ) : (
                    part
                )
            );
        } catch (e) {
            return text;
        }
    }, []);

    const enhancedRenderOption = useCallback(
        (props, option) => {
            if (renderOption) {
                return renderOption(props, option, { inputValue, highlightText });
            }
            const { key, ...liProps } = props;
            const optionLabel = latest.current.getOptionLabel(option);
            return (
                <Box component="li" {...liProps} key={option?._id || key}>
                    <Typography noWrap>{highlightText(optionLabel, inputValue)}</Typography>
                </Box>
            );
        },
        [renderOption, inputValue, highlightText]
    );

    return (
        <Autocomplete
            {...otherProps}
            open={isOpen}
            onOpen={() => setIsOpen(true)}
            onClose={() => setIsOpen(false)}
            options={displayOptions}
            loading={loading}
            disabled={disabled}
            value={value ?? null}
            onChange={onChange}
            inputValue={inputValue}
            onInputChange={handleInputChange}
            isOptionEqualToValue={isOptionEqualToValue || defaultIsOptionEqualToValue}
            getOptionLabel={getOptionLabel || defaultGetOptionLabel}
            filterOptions={passthroughFilter}
            noOptionsText={loading ? 'Searching...' : noOptionsText}
            ListboxProps={{ onScroll: handleScroll }}
            clearOnBlur={false}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={label}
                    error={error}
                    helperText={helperText}
                    placeholder={placeholder}
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <>
                                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                                {params.InputProps.endAdornment}
                            </>
                        ),
                    }}
                />
            )}
            renderOption={enhancedRenderOption}
        />
    );
};

export default OptimizedSearchableAutocomplete;